/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import MilestoneType from '@linc-technologies/linced-common/enums/milestone-type';
import ProgressionJudgement from '@linc-technologies/linced-common/enums/progression-judgement';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import { calculateYearLevelMilestoneDate } from '@linc-technologies/linced-common/utils/progressions';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import moment from 'moment';
import { classNames } from '@ember-decorators/component';
import { observes } from '@ember-decorators/object';

@classNames('otj-widget')
export default class OtjWidget extends Component {
  // Services
  @service
  dates;

  @service
  permissions;

  @service
  session;

  @service
  store;

  @service
  toaster;

  ProgressionJudgement = ProgressionJudgement;

  // Props
  // type provides the type of progression to be reporting for.
  // Defaults to Progressions.
  type = ProgressionType.PROGRESSION;

  // milestones provides the milestones that the widget will update the overall
  // teacher judgement for.
  milestones = null;

  // currentMilestone provides the current milestone that the widget for the
  // overall teacher judgement.
  currentMilestone = null;

  // levels provides the levels that the widget will update the overall teacher
  // judgement for.
  levels = null;

  // expectations provides the raw expectations for all milestones that the
  // widget will use to update the specific overall teacher judgement for.
  expectations = null;

  // records provides the records that the widget will update the overall
  // teacher judgement for.
  records = null;

  // person provides the person that the widget will update the overall teacher
  // judgement for.
  person = null;

  // State
  draggingLevelSlider = false;

  // previousLevel is used to revert state in the case of errors. It holds
  // the current level while the user is in the process of selecting or saving
  // a new level.
  @tracked
  previousLevel = null;

  // selectedLevel is the current progression level selected on the slider.
  @tracked
  selectedLevel = null;

  // deleteProgressionDialogOpen toggles the delete progression dialog.
  @tracked
  deleteProgressionDialogOpen = false;

  @action
  setDeleteProgressionDialogOpen(value) {
    this.deleteProgressionDialogOpen = value;
  }

  // progressionErrorDialogOpen toggles the create progression error dialog.
  @tracked
  progressionErrorDialogOpen = false;

  // progressionErrorCodes contains a string of error response codes from progression creation.
  @tracked
  progressionErrorCodes = '';

  @tracked
  deleteInProgress;

  // _watchLevelChange observes changes on the level slider.
  @observes('draggingLevelSlider')
  _watchLevelChange() {
    if (this._levelChanged()) {
      this.setLevel(this.selectedLevel);
    }
  }

  // currentRecord calculates the current progress record based on milestone.
  @computed('records.[]', 'currentMilestone.id')
  get currentRecord() {
    if (this.records && this.currentMilestone.id) {
      return findBy(this.records, 'milestone_id', this.currentMilestone.id);
    }
    return null;
  }

  // recordsByMilestone maps each milestone id to its progress record. The
  // milestone line reads it once for each milestone.
  @computed('records.[]')
  get recordsByMilestone() {
    const map = new Map();
    for (const record of this.records ?? []) {
      // Keep the first record, as find-by did.
      if (!map.has(record.milestone_id)) {
        map.set(record.milestone_id, record);
      }
    }
    return map;
  }

  // recordFor gives the progress record of a milestone, or undefined.
  recordFor = (milestoneId) => this.recordsByMilestone.get(milestoneId);

  // Class Methods
  // setLevel saves the newly recorded overall teacher judgement.
  setLevel(newLevel) {
    if (isEmpty(newLevel)) {
      // set back to previous level, as we no longer allow deletes via the
      // slider.
      this.selectedLevel = this.previousLevel;
      return;
    }

    return this.createProgression(newLevel);
  }

  // createProgression creates a new data point for a teacher judgement,
  // marking student progress.
  createProgression(newLevel) {
    let progression = {
      schoolId: this.session.school,
      personId: this.person,
      assessorId: this.session.person,
      milestoneId: this.currentMilestone,
      dueDate: this.getDueDate(this.person, this.currentMilestone),
      progressionType: this.type,
      goalSubjectId: this.subject,
      levelId: newLevel,
    };
    let expectation = findBy(
      this.expectations,
      'value',
      this.currentMilestone.id,
    );
    if (expectation) {
      let index = this.levels.indexOf(newLevel);
      progression.judgement = this.getJudgement(index, expectation);
    }

    return this.store
      .createRecord('milestones/progression', progression)
      .save()
      .then(() => {
        // mark the new level as the new point of reference going forward.
        this.previousLevel = newLevel;
        if (this.onJudgementChange && progression.judgement != null) {
          this.onJudgementChange(progression.judgement);
        }
      })
      .catch((err) => {
        this.progressionErrorCodes = this._getErrorStatuses(err);
        this.progressionErrorDialogOpen = true;
        // reset the selected level to the previous level
        this.selectedLevel = this.previousLevel;
      })
      .finally(() => {
        this._updateRecords();
        this._updateChangelog();
      });
  }

  // TODO: put this somewhere common if we reuse it
  _getErrorStatuses(err) {
    return err.errors
      .map((error) => {
        return error.status;
      })
      .join(',');
  }

  // getDueDate calculates when an overall teacher judgement is due.
  getDueDate(person, milestone) {
    let dueDate = moment();
    let year = dueDate.year();

    if (milestone.type !== MilestoneType.YEAR_LEVEL) {
      if (isEmpty(person.firstSchooling)) {
        // No first schooling date defined, but we need one.
        // We shouldn't get here, but if we do, just return today.
        return dueDate.format('YYYY-MM-DD');
      } else {
        // Start with the first schooling date.
        // We'll add the number of weeks or months on to it.
        dueDate = moment(person.firstSchooling);
      }
    }

    switch (milestone.type) {
      case MilestoneType.WEEKS_AT_SCHOOL:
        dueDate.add(milestone.duration, 'weeks');
        break;

      case MilestoneType.MONTHS_AT_SCHOOL:
        dueDate.add(milestone.duration, 'months');
        break;

      case MilestoneType.YEAR_LEVEL:
        year = year - person.numericCurrentYearLevel + milestone.yearLevel;
        dueDate = calculateYearLevelMilestoneDate(
          year,
          milestone,
          this.dates.rolloverMonthDate,
        );
        break;
    }

    return dueDate.format('YYYY-MM-DD');
  }

  // getJudgement returns an overall teacher judgement.
  getJudgement(index, expectation) {
    let judgement;
    switch (true) {
      case index <= expectation.data['Below']:
        judgement = ProgressionJudgement.WELL_BELOW;
        break;

      case index <= expectation.data['At']:
        judgement = ProgressionJudgement.BELOW;
        break;

      case index <= expectation.data['Above']:
        judgement = ProgressionJudgement.AT;
        break;

      case index <= expectation.data['Well above']:
        judgement = ProgressionJudgement.ABOVE;
        break;

      case index > expectation.data['Well above']:
        judgement = ProgressionJudgement.WELL_ABOVE;
        break;
    }

    return judgement;
  }

  // deleteProgression removes a single data point of student progress.
  deleteProgression() {
    this.deleteInProgress = true;
    return this.currentRecord
      .destroyRecord()
      .then(() => this._notify())
      .catch((err) => this._notify(err))
      .then(() => this._updateRecords())
      .then(() => this._updateChangelog())
      .finally(() => (this.deleteInProgress = false));
  }

  // _levelChanged detects if the selected level has changed.
  _levelChanged() {
    return (
      !this.draggingLevelSlider && this.selectedLevel !== this.previousLevel
    );
  }

  // _notify informs the user of success or fail.
  _notify(err) {
    if (isEmpty(err)) {
      this.toaster.success(`Progression deleted`);
      return;
    }
    this.toaster.error(`Error deleting progression!`);
  }

  // _reset clears out the widget.
  _reset() {
    this.previousLevel = null;
    this.selectedLevel = null;
  }

  // _updateRecords runs the update records callback if provided, or updates
  // the records locally.
  _updateRecords() {
    if (!isEmpty(this.updateRecords)) {
      this.updateRecords();
    } else {
      this.records.then((records) => {
        records.update();
      });
    }
  }

  // _updateChangelog runs the update changelog callback
  _updateChangelog() {
    if (!isEmpty(this.updateChangelog)) {
      this.updateChangelog();
    }
  }

  @computed('session.scopes.[]')
  get isAdmin() {
    return this.permissions.meet('urn:linced:organisation:role:admin');
  }

  // changeLevel provides an action to change the current level to the
  // selected level.
  @action
  changeLevel(newLevel) {
    this.selectedLevel = newLevel;
    if (this._levelChanged()) {
      // Handle each discrete keyboard change event.
      this.setLevel(newLevel);
    }
  }

  // startChangeLevel provides an action to track slider drag start.
  @action
  startChangeLevel() {
    this.previousLevel = this.selectedLevel;
    this.set('draggingLevelSlider', true);
  }

  // endChangeLevel provides an action to track slider drag end.
  @action
  endChangeLevel() {
    this.set('draggingLevelSlider', false);
  }

  // confirmDelete deletes a progression.
  @action
  confirmDelete(confirmed) {
    if (confirmed) {
      this.deleteInProgress = true;
      this._reset();
      this.deleteProgression();
    }

    this.deleteProgressionDialogOpen = false;
  }

  // Lifecycle Hooks
  didReceiveAttrs() {
    // Set current level (if applicable) when received from parent.
    super.didReceiveAttrs(...arguments);
    if (this.currentRecord) {
      let levelId = this.get('currentRecord.level.id');
      let currentLevel = findBy(this.levels, 'id', levelId);
      this.previousLevel = currentLevel;
      this.selectedLevel = currentLevel;
      return;
    }

    this._reset();
  }
}
